import {
  type AsyncResult,
  createNotRequestedAsyncResult,
  createPendingAsyncResult,
  createResolvedAsyncResult,
} from "@/cache/AsyncResult";
import { type CacheDatum, CacheDatumState, type LoadedCacheDatum } from "@/cache/CacheDatumState";
import { type Stats, defaultCacheFormatter, jsxTable } from "@/cache/Stats";
import { evictCacheEntriesBySize, markCacheDatumUsed } from "@/cache/utils/cacheEviction";
import { createLoadedCacheDatum, sumLoadedCacheMemoryConsumption } from "@/cache/utils/cacheMemory";
import { mapboxIconsThreadPool } from "@/threads/MapboxIconsThread/MapboxIconsThreadPool";
import type { IconSlice } from "@/threads/MapboxIconsThread/types";
import { MiB } from "@/utility/bytesize";

export interface MaptilerSpritesStats {
  count: number;
  pending: number;
  estimatedMemoryConsumption: number;
}

const DEFAULT_MAPTILER_SPRITE_CACHE_MAX_SIZE = MiB(64);
const DEFAULT_MAPTILER_SPRITE_CACHE_DELAY_EVICTION_SIZE = MiB(96);

export class MaptilerSpritesCache implements Stats<MaptilerSpritesStats> {
  protected cache: { [key: string]: CacheDatum<IconSlice[]> } = {};

  constructor(
    private readonly label_: string,
    private readonly maxSize: number = DEFAULT_MAPTILER_SPRITE_CACHE_MAX_SIZE,
    private readonly delayEvictionUntilSize: number = DEFAULT_MAPTILER_SPRITE_CACHE_DELAY_EVICTION_SIZE,
  ) {}

  stats(): MaptilerSpritesStats {
    let count = 0;
    let pending = 0;
    const cache = this.cache;

    for (const line in cache) {
      if (Object.hasOwn(cache, line)) {
        count++;

        if (cache[line].state === CacheDatumState.Pending) {
          pending++;
        }
      }
    }

    return {
      count,
      pending,
      estimatedMemoryConsumption: sumLoadedCacheMemoryConsumption(cache),
    };
  }

  descJsx(): JSX.Element {
    return <>Special-purpose cache for Maptiler icon sprites. Replacement policy is approximate memory-based LRU.</>;
  }

  label(): string {
    return this.label_;
  }

  clear() {
    this.cache = {};
  }

  private markUsed(cacheLine: LoadedCacheDatum<IconSlice[]>) {
    markCacheDatumUsed(cacheLine);
  }

  private unloadCacheEntry(_: CacheDatum<IconSlice[]> | undefined, cacheId: string) {
    delete this.cache[cacheId];
  }

  private pruneCacheEntries() {
    evictCacheEntriesBySize(this.cache, this.maxSize, this.delayEvictionUntilSize, this.unloadCacheEntry.bind(this));
  }

  statsJsx(): JSX.Element {
    return jsxTable(this.stats(), defaultCacheFormatter);
  }

  /**
   * A method to return a cached data from cache containing raw parameter values.
   * It does not trigger a new request when the given request hasn't been emitted.
   * @param cacheId
   * @returns
   */
  peekDataCache(cacheId: string): AsyncResult<IconSlice[]> {
    const v = this.cache[cacheId];
    if (!v) {
      return createNotRequestedAsyncResult([]);
    }
    switch (v.state) {
      case CacheDatumState.Pending: {
        return createPendingAsyncResult(v.promise);
      }
      case CacheDatumState.PermanentlyFailed:
        return createResolvedAsyncResult([]);
      case CacheDatumState.Loaded:
        this.markUsed(v);
        return createResolvedAsyncResult(v.payload);
      default: {
        const _exhaustive: never = v;
        return _exhaustive;
      }
    }
  }

  loadSpriteSheet(id: string, dpi: number): AsyncResult<IconSlice[]> {
    const keySuffix = dpi > 1 ? "@2x" : "@1x";

    const cacheId = `${id}${keySuffix}`;
    if (Object.hasOwn(this.cache, cacheId)) {
      return this.peekDataCache(cacheId);
    }

    const requestDatetime = performance.now();
    const promise = mapboxIconsThreadPool
      .loadAndSliceSprite(id, dpi)
      .then((payload) => {
        const cacheLine = this.cache[cacheId];
        if (
          !cacheLine ||
          cacheLine.state !== CacheDatumState.Pending ||
          cacheLine.requestDatetime !== requestDatetime
        ) {
          return payload.data;
        }

        const cacheData: LoadedCacheDatum<IconSlice[]> = createLoadedCacheDatum(payload.data, requestDatetime);
        this.cache[cacheId] = cacheData;
        this.markUsed(cacheData);
        this.pruneCacheEntries();

        return payload.data;
      })
      .catch((error) => {
        const cacheLine = this.cache[cacheId];
        if (
          !cacheLine ||
          cacheLine.state !== CacheDatumState.Pending ||
          cacheLine.requestDatetime !== requestDatetime
        ) {
          return Promise.reject(error);
        }

        this.cache[cacheId] = {
          requestDatetime,
          state: CacheDatumState.PermanentlyFailed,
          payload: null,
        };

        return Promise.reject(error);
      });

    this.cache[cacheId] = {
      requestDatetime,
      state: CacheDatumState.Pending,
      promise,
      payload: null,
    };
    return createPendingAsyncResult(promise);
  }
}
